import React, { useState } from "react";
import { Card, CardHeader, CardBody, CardFooter, Link } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DeleteWorkflowModal } from "./DeleteWorkflowModal";
import { CloneWorkflowModal } from "./CloneWorkflowModal";

interface WorkflowCardProps {
  id: string;
  title: string;
  description: string;
  icon: string | undefined | null;
  onDelete: (id: string) => Promise<void>;
  onClone: (id: string) => Promise<void>;
  canEdit?: boolean;
  canDelete?: boolean;
  canOpenInPlayground?: boolean;
}

export const WorkflowCard: React.FC<WorkflowCardProps> = ({
  id,
  title,
  description,
  icon,
  onDelete,
  onClone,
  canEdit = true,
  canDelete = false,
  canOpenInPlayground = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  // const handleMonitoringClick = () => navigate(`/workflows/${id}/metrics`);
  const handleSettingsClick = () => navigate(`/workflows/${id}/edit`);
  const handlePlaygroundClick = () => navigate(`/workflows/playground/${id}`);
  const handleCardClick = () => navigate(`/workflows/${id}`);

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloneClick = () => {
    setIsCloneModalOpen(true);
  };

  return (
    <Card
      isPressable
      className="py-1 hover:shadow-lg hover:scale-102 transition-all duration-200"
      onPress={handleCardClick}
    >
      <CardHeader className="pb-0 pt-2 px-4 flex-col items-start">
        <h4 className="text-brand-primary font-bold text-large text-center w-full">
          {t(`workflowIndex.${title}`, { defaultValue: title })}
        </h4>
        <small className="text-default-500 text-left line-clamp-2">
          {t(`workflowIndex.${title}_desc`, { defaultValue: description })}
        </small>
      </CardHeader>
      <CardBody className="overflow-visible py-2 flex items-center justify-center">
        {icon && (
          <Icon
            className="text-brand-primary text-xl text-foreground hover:text-primary"
            height={80}
            icon={icon}
          />
        )}
        {/* {icon && (
        {/* {icon && (
          <Image
            alt="Card background"
            className="object-cover rounded-xl shadow-md"
            src={icon}
            width={270}
            height={150}
          />
        )} */}
      </CardBody>
      <CardFooter className="flex justify-end gap-1 pb-0">
        <Link
          aria-label="Clone"
          className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
          onPress={handleCloneClick}
        >
          <Icon
            className="text-xl text-foreground hover:text-primary"
            icon="ic:baseline-content-copy"
          />
        </Link>
        {canDelete && (
          <Link
            aria-label="Delete"
            className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
            onPress={handleDeleteClick}
          >
            <Icon className="text-xl text-foreground hover:text-danger" icon="ic:baseline-delete" />
          </Link>
        )}
        {canEdit && canOpenInPlayground && (
          <Link
            aria-label="Open in Playground"
            className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
            onPress={handlePlaygroundClick}
          >
            <Icon
              className="text-xl text-foreground hover:text-primary"
              icon="solar:play-circle-linear"
            />
          </Link>
        )}
        {canEdit && (
          <Link
            aria-label="Settings"
            className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
            onPress={handleSettingsClick}
          >
            <Icon
              className="text-xl text-foreground hover:text-primary"
              icon="ic:baseline-settings"
            />
          </Link>
        )}
      </CardFooter>
      <CloneWorkflowModal
        isOpen={isCloneModalOpen}
        workflowId={id}
        workflowName={title}
        onClose={() => setIsCloneModalOpen(false)}
        onConfirm={onClone}
      />
      <DeleteWorkflowModal
        isOpen={isDeleteModalOpen}
        workflowId={id}
        workflowName={title}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={onDelete}
      />
    </Card>
  );
};
